import React from 'react';
import { Calendar, Gauge, CheckCircle } from 'lucide-react';

/**
 * KioskVehicleCard - Vehicle card optimized for kiosk display
 * Larger touch targets, no favorites, simplified for in-dealership use
 */
const KioskVehicleCard = ({ vehicle, onSelect }) => {
  // Format mileage
  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price);
  };

  // Determine condition tags
  const tags = [];
  if (vehicle.featured) tags.push({ label: 'Featured', color: 'app-accent' });
  if (vehicle.mileage < 40000) tags.push({ label: 'Low Mileage', color: 'app-success' });
  if (vehicle.bookingCount && vehicle.bookingCount > 50) tags.push({ label: 'Popular Choice', color: 'app-warning' });

  return (
    <div
      onClick={() => onSelect(vehicle._id)}
      className="bg-app-bg-secondary-light dark:bg-app-bg-secondary rounded-2xl border-2 border-app-border-light dark:border-app-border overflow-hidden hover:border-app-accent transition-all duration-200 hover:shadow-2xl cursor-pointer group"
    >
      <div className="md:flex">
        {/* Vehicle Image - Left side on desktop */}
        <div className="relative md:w-96 h-64 md:h-auto bg-app-bg-tertiary-light dark:bg-app-bg-tertiary overflow-hidden flex-shrink-0">
          <img
            src={vehicle.image || 'https://via.placeholder.com/400x300?text=Vehicle+Image'}
            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />

          {/* Condition Tags */}
          {tags.length > 0 && (
            <div className="absolute bottom-4 left-4 flex flex-wrap gap-2">
              {tags.map((tag, idx) => (
                <div
                  key={idx}
                  className={`bg-${tag.color} text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg`}
                >
                  {tag.label}
                </div>
              ))}
            </div>
          )}

          {/* Available indicator */}
          <div className="absolute top-4 right-4 bg-app-success text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-lg flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            <span>Available Now</span>
          </div>
        </div>

        {/* Content - Right side on desktop */}
        <div className="flex-1 p-6 md:p-8 flex flex-col">
          {/* Vehicle Title & Price */}
          <div className="mb-4">
            <h3 className="text-2xl md:text-3xl font-bold text-app-text-light dark:text-app-text mb-2 group-hover:text-app-accent transition-colors">
              {vehicle.year} {vehicle.make} {vehicle.model}
            </h3>
            {vehicle.price && (
              <p className="text-3xl md:text-4xl font-bold text-app-accent">
                {formatPrice(vehicle.price)}
              </p>
            )}
          </div>

          {/* Vehicle Stats */}
          <div className="flex flex-wrap items-center gap-6 mb-6 text-base md:text-lg text-app-text-secondary-light dark:text-app-text-secondary">
            <div className="flex items-center gap-2">
              <Calendar className="w-6 h-6" />
              <span className="font-medium">{vehicle.year}</span>
            </div>
            <div className="flex items-center gap-2">
              <Gauge className="w-6 h-6" />
              <span className="font-medium">{formatMileage(vehicle.mileage)} miles</span>
            </div>
            {vehicle.type && (
              <div className="px-4 py-2 bg-app-bg-tertiary-light dark:bg-app-bg-tertiary rounded-lg text-base font-semibold">
                {vehicle.type}
              </div>
            )}
            {vehicle.transmission && (
              <span className="font-medium">{vehicle.transmission}</span>
            )}
          </div>

          {/* Additional Details */}
          {vehicle.fuel && (
            <div className="mb-6">
              <span className="text-base text-app-text-light dark:text-app-text">
                <span className="font-semibold">Fuel Type:</span> {vehicle.fuel}
              </span>
            </div>
          )}

          {/* CTA Section */}
          <div className="mt-auto pt-6 border-t border-app-border-light dark:border-app-border">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 bg-app-success rounded-full animate-pulse"></div>
                <span className="text-lg font-semibold text-app-success">
                  Ready for Test Drive
                </span>
              </div>

              <div className="bg-app-accent hover:bg-app-accent/90 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all duration-200 hover:shadow-lg">
                Select Vehicle →
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KioskVehicleCard;
